import type { UUID } from '@/shared/types/api';

/**
 * Marks the current browser session as a demo walkthrough.
 *
 * Purely a UI concern: it decides whether the "demo" affordances are shown and
 * remembers which charge the guide should offer to pay. Authority still rests
 * entirely with the JWT — clearing this changes nothing about what the session
 * is allowed to do.
 */
const STORAGE_KEY = 'charjer.demo';

export interface DemoSession {
  /** The outstanding charge the reviewer is invited to pay. */
  unitChargeId: UUID | null;
  /** The demo building, for the "explore management" links. */
  buildingId: UUID | null;
}

function isDemoSession(value: unknown): value is DemoSession {
  return typeof value === 'object' && value !== null && 'unitChargeId' in value;
}

export const demoSession = {
  read(): DemoSession | null {
    try {
      const raw = window.sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed: unknown = JSON.parse(raw);
      return isDemoSession(parsed) ? parsed : null;
    } catch {
      return null;
    }
  },

  start(session: DemoSession): void {
    try {
      // sessionStorage, not localStorage: a demo should not outlive the tab it
      // was opened in and leak its banner into someone's real session later.
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      /* the walkthrough still works, just without the persistent hint */
    }
  },

  clear(): void {
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to clean up */
    }
  },

  isActive(): boolean {
    return demoSession.read() !== null;
  },
} as const;
